import React, { useState, useEffect, useCallback } from 'react';
import { adminGet, adminPost, adminPatch, adminDelete } from '../api';
import ConfirmDialog from './ConfirmDialog';

export default function GenreManager() {
  const [genres,    setGenres]   = useState([]);
  const [loading,   setLoading]  = useState(true);
  const [error,     setError]    = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editName,  setEditName]  = useState('');
  const [newName,   setNewName]   = useState('');
  const [adding,    setAdding]    = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try { setGenres(await adminGet('/api/admin/movie-genres')); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await adminPost('/api/admin/movie-genres', { name: newName.trim() });
      setNewName('');
      setAdding(false);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async (id) => {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      await adminPatch(`/api/admin/movie-genres/${id}`, { name: editName.trim() });
      setEditingId(null);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await adminDelete(`/api/admin/movie-genres/${delTarget.id}`);
      setDelTarget(null);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div>
      <h2 className="admin-page-title">Movie Genres</h2>

      <div className="admin-action-bar">
        {!adding && (
          <button className="btn btn-primary" onClick={() => setAdding(true)}>+ Add genre</button>
        )}
        <button className="btn btn-secondary" onClick={load}>Refresh</button>
      </div>

      {error   && <div className="admin-banner admin-banner-error">{error}</div>}
      {loading && <div style={{ color: '#888' }}>Loading…</div>}

      {adding && (
        <form onSubmit={handleAdd} style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <input
            className="admin-search"
            type="text"
            placeholder="Genre name…"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            autoFocus
            required
          />
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Add'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => { setAdding(false); setNewName(''); }}>
            Cancel
          </button>
        </form>
      )}

      {!loading && (
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Sort</th>
              <th style={{ width: 160 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {genres.length === 0 ? (
              <tr><td colSpan={4} style={{ color: '#888', textAlign: 'center' }}>No genres yet</td></tr>
            ) : genres.map(g => (
              <tr key={g.id}>
                <td>{g.id}</td>
                <td>
                  {editingId === g.id ? (
                    <input
                      type="text"
                      value={editName}
                      onChange={e => setEditName(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleRename(g.id)}
                      autoFocus
                      style={{ padding: '4px 8px', border: '1px solid #ccc', borderRadius: 4, fontSize: 13 }}
                    />
                  ) : g.name}
                </td>
                <td>{g.sort_order}</td>
                <td>
                  <div style={{ display: 'flex', gap: 5 }}>
                    {editingId === g.id ? (
                      <>
                        <button className="btn btn-sm btn-primary" onClick={() => handleRename(g.id)} disabled={saving}>Save</button>
                        <button className="btn btn-sm btn-secondary" onClick={() => setEditingId(null)}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <button className="btn btn-sm btn-secondary" onClick={() => { setEditingId(g.id); setEditName(g.name); }}>Rename</button>
                        <button className="btn btn-sm btn-danger"    onClick={() => setDelTarget(g)}>Delete</button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <ConfirmDialog
        open={Boolean(delTarget)}
        title={`Delete genre "${delTarget?.name}"?`}
        message="Movies in this genre will lose their genre association. This cannot be undone."
        onConfirm={handleDelete}
        onCancel={() => setDelTarget(null)}
        confirmLabel="Delete genre"
      />
    </div>
  );
}
